import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";

/** Opciones habituales de uso urbanístico; el agente puede escribir otra. */
export const OPCIONES_USO_PERMITIDO = [
  "Residencial",
  "Residencial unifamiliar",
  "Residencial plurifamiliar",
  "Terciario",
  "Comercial",
  "Industrial",
  "Hotelero",
  "Dotacional",
  "Mixto",
  "Agrícola",
];

/** Clasificación urbanística habitual; el agente puede escribir otra. */
export const OPCIONES_CLASIFICACION_URB = ["Urbana", "Urbanizable", "No urbanizable"];

export const CAMPOS_URBANISMO = [
  {
    key: "urb_clasificacion",
    label: "Clasificación urbanística",
    tipo: "combobox",
    opciones: OPCIONES_CLASIFICACION_URB,
    placeholder: "Elige o escribe…",
  },
  {
    key: "urb_uso_permitido",
    label: "Uso permitido",
    tipo: "combobox",
    opciones: OPCIONES_USO_PERMITIDO,
    placeholder: "Elige o escribe…",
  },
  { key: "urb_edificabilidad", label: "Edificabilidad (M2)", tipo: "text", placeholder: "Ej: 0,5 o 250" },
  { key: "urb_ocupacion_max", label: "Ocupación máxima (%)", tipo: "number", placeholder: "0", sufijo: "%" },
  { key: "urb_alturas_max", label: "Alturas / plantas máximas", tipo: "text", placeholder: "Ej: 2 plantas + ático" },
  { key: "urb_retranqueos", label: "Retranqueos (ML)", tipo: "text", placeholder: "Frente, laterales, fondo…" },
  { key: "urb_frente_parcela", label: "Frente de parcela (m)", tipo: "number", placeholder: "m" },
  { key: "urb_max_viviendas", label: "Nº máximo de viviendas", tipo: "number", placeholder: "ud" },
  { key: "urb_cesiones", label: "Cesiones / aprovechamiento", tipo: "text" },
  { key: "urb_planeamiento", label: "Planeamiento aplicable", tipo: "text", placeholder: "PGOU, sector, UE…" },
  { key: "urb_observaciones", label: "Observaciones urbanísticas", tipo: "textarea" },
];

export function urbanismoTieneDatos(form) {
  if (!form) return false;
  return CAMPOS_URBANISMO.some((c) => {
    const v = form[c.key];
    return v !== null && v !== undefined && String(v).trim() !== "";
  });
}

export default function ModalUrbanismo({ open, onClose, form, cambiar, cambiarDirecto, disabled = false }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    border: `1px solid ${c.chipBorder || c.border}`,
    borderRadius: 8,
    fontSize: 13,
    color: c.textPrimary,
    background: isDark ? "#0a0e16" : "#fff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const modal = (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12000,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        ref={dragRef}
        role="dialog"
        aria-label="Urbanismo"
        onClick={(e) => e.stopPropagation()}
        style={{
          ...panelPositionStyle,
          width: "min(560px, 96vw)",
          maxHeight: "90vh",
          overflow: "auto",
          background: isDark ? "#151b28" : "#fff",
          borderRadius: 12,
          border: `1px solid ${c.chipBorder || c.border}`,
          boxShadow: "0 16px 48px rgba(0,0,0,0.28)",
        }}
      >
        <div
          onMouseDown={startDrag}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "14px 16px",
            borderBottom: `1px solid ${c.chipBorder || c.border}`,
            cursor: "grab",
            userSelect: "none",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 15, color: c.textPrimary }}>Urbanismo</span>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 12,
              fontFamily: "inherit",
              background: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
              color: c.textPrimary,
            }}
          >
            Cerrar
          </button>
        </div>

        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          {CAMPOS_URBANISMO.map((campo) => (
            <label key={campo.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, letterSpacing: "0.02em" }}>
                {campo.label}
              </span>
              {campo.tipo === "select" ? (
                <select
                  name={campo.key}
                  value={form?.[campo.key] ?? ""}
                  disabled={disabled}
                  onChange={(e) => {
                    if (cambiarDirecto) cambiarDirecto(campo.key, e.target.value || null);
                    else if (cambiar) cambiar(e);
                  }}
                  style={{ ...inputStyle, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
                >
                  <option value="">—</option>
                  {campo.opciones.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              ) : campo.tipo === "combobox" ? (
                <>
                  <input
                    type="text"
                    name={campo.key}
                    list={`dl-urb-${campo.key}`}
                    value={form?.[campo.key] ?? ""}
                    onChange={cambiar}
                    disabled={disabled}
                    placeholder={campo.placeholder || "Elige o escribe…"}
                    style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                    autoComplete="off"
                  />
                  <datalist id={`dl-urb-${campo.key}`}>
                    {(campo.opciones || []).map((op) => (
                      <option key={op} value={op} />
                    ))}
                  </datalist>
                </>
              ) : campo.tipo === "textarea" ? (
                <textarea
                  name={campo.key}
                  rows={3}
                  value={form?.[campo.key] ?? ""}
                  onChange={cambiar}
                  disabled={disabled}
                  placeholder={campo.placeholder || ""}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 72, lineHeight: 1.45, opacity: disabled ? 0.6 : 1 }}
                />
              ) : campo.sufijo ? (
                <div style={{ position: "relative" }}>
                  <input
                    type={campo.tipo === "number" ? "number" : "text"}
                    name={campo.key}
                    value={form?.[campo.key] ?? ""}
                    onChange={cambiar}
                    disabled={disabled}
                    placeholder={campo.placeholder || ""}
                    style={{
                      ...inputStyle,
                      paddingRight: 36,
                      opacity: disabled ? 0.6 : 1,
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: c.textMuted,
                      fontSize: 13,
                      fontWeight: 700,
                      pointerEvents: "none",
                    }}
                  >
                    {campo.sufijo}
                  </span>
                </div>
              ) : (
                <input
                  type={campo.tipo === "number" ? "number" : "text"}
                  name={campo.key}
                  value={form?.[campo.key] ?? ""}
                  onChange={cambiar}
                  disabled={disabled}
                  placeholder={campo.placeholder || ""}
                  style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                />
              )}
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
